/**
 * Shaping `agent.messages` for the transcript view.
 *
 * `CopilotChatMessageView` walks its `messages` prop once and renders one block
 * per message. That is the right model, but the SDK splits one logical answer
 * across several assistant messages — an empty-content row carrying a batch of
 * tool calls, then a row with the prose — so rendering them one-to-one puts an AI
 * avatar next to each fragment instead of one next to the answer.
 *
 * `deduplicateMessages` inside the view does not help: it merges by message id,
 * and these fragments have different ids.
 *
 * So the merge happens here, in the data, before the view sees it. The
 * alternative — deciding per message whether to draw an avatar, by looking back
 * at its neighbours from inside the slot component — needs an invisible
 * fixed-width avatar spacer to keep the indent, i.e. a pixel value duplicated
 * from Cloudscape's Avatar. A pure list transform has no such coupling and can be
 * tested on its own.
 */

import { INLINE_TOOL_NAMES, SUGGEST_REPLIES_TOOL } from './hitlToolNames';

/** The subset of an AG-UI message this module reads. */
export interface TranscriptMessage {
  id: string;
  role: string;
  content?: unknown;
  toolCallId?: string;
  toolCalls?: Array<{
    id: string;
    type?: string;
    function?: { name?: string; arguments?: string };
  }>;
  /** Only on a merged assistant head — see `AssistantBlock`. */
  blocks?: AssistantBlock[];
  /** Only on a merged assistant head — see `isProcessNarration`. */
  narrations?: Narration[];
}

/** A short process-narration sentence, shown in the turn's trace before a tool row. */
export interface Narration {
  beforeToolCallId: string;
  text: string;
}

/**
 * One renderable piece of a merged assistant run, in the order the model
 * produced it.
 *
 * The merge below flattens several assistant fragments into one message so the
 * answer gets one avatar. That loses information the layout needs: WHERE in the
 * prose each approval card belongs. CopilotKit's own assistant message renders
 * `markdownRenderer` then `toolCallsView` (`CopilotChatAssistantMessage`), i.e.
 * a message's tool calls come after that message's text — correct per fragment,
 * but meaningless once the fragments are one string. A run that emitted
 * `[table] → approve_audience_segment → "收到的选择是…"` can only be rendered
 * right if the boundaries survive the merge.
 *
 * Only inline tool calls (`INLINE_TOOL_NAMES`: the gates plus result cards such
 * as `file_send`) split a text block. Every other call goes into a `trace` block:
 * the collapsed `ToolTrace` diagnostic accordion. There is one trace per
 * *segment* — the stretch between two gate blocks — and it always sits at the
 * start of its segment, so a turn reads `[trace][text][gate][trace][text]`.
 * Per segment rather than per turn because a HITL resume continues the SAME
 * merged message: with one turn-wide trace, the work done after the user
 * answered a card was appended to the trace above the card (and reopened it as
 * "Thinking..."), instead of showing up below the card where it happened. A turn
 * with no inline call is a single segment, so it renders identically to before.
 */
export type AssistantBlock =
  | { kind: 'text'; text: string }
  | { kind: 'gates'; toolCallIds: string[] }
  | { kind: 'trace'; toolCallIds: string[] };

/**
 * Flatten an AG-UI message's content to text.
 *
 * Content is a string in the normal case, but the AG-UI schema also allows an
 * array of typed parts, and the Gateway forwards whatever the model produced.
 * Non-text parts (images, documents) are dropped — the transcript renders
 * markdown.
 */
export function messageText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return (content as Array<{ type?: string; text?: string }>)
      .filter(p => p.type === 'text')
      .map(p => p.text ?? '')
      .join('');
  }
  return '';
}

/** What `coalesceAssistantRuns` produces. */
export interface CoalescedTranscript {
  messages: TranscriptMessage[];
  /**
   * head message id → every id that was merged into it, the head included.
   *
   * Needed because the display-extras side table is keyed by the id of the row
   * the data came from, which is not necessarily the id the merged message ends
   * up with. On restored history a turn replays as
   * `[<toolRow>-call, <toolRow>-result, <finalResponse>]`, so the merged bubble
   * carries the synthesized `-call` id while the ⏱ badge was computed against
   * the final response's id. Without this map every restored turn that used a
   * tool would silently lose its badge — the contract doc already recorded that
   * happening to gate-bearing bubbles, and rendering all tool calls would have
   * spread it to every turn.
   */
  mergedIds: Map<string, string[]>;
}

/** The inline tool calls (gates, result cards) one fragment declared, in call order. */
function gateIdsOf(m: TranscriptMessage): string[] {
  const ids: string[] = [];
  for (const tc of m.toolCalls ?? []) {
    const name = tc.function?.name;
    if (name && INLINE_TOOL_NAMES.has(name)) ids.push(tc.id);
  }
  return ids;
}

/** The ordinary (trace) tool calls one fragment declared, in call order. */
function traceIdsOf(m: TranscriptMessage): string[] {
  const ids: string[] = [];
  for (const tc of m.toolCalls ?? []) {
    const name = tc.function?.name;
    if (!name || !INLINE_TOOL_NAMES.has(name)) ids.push(tc.id);
  }
  return ids;
}

/** Index of the first block of the open segment — the one after the last gate block. */
export function openSegmentStart(blocks: AssistantBlock[]): number {
  let start = blocks.length;
  while (start > 0 && blocks[start - 1].kind !== 'gates') start--;
  return start;
}

/**
 * Add trace calls to the open segment's trace, creating it at the segment start.
 *
 * At the start rather than appended, so the text already in the segment keeps
 * being the last block — `appendBlocks` joins the next fragment onto it, which is
 * what keeps a gateless turn one bubble.
 */
function addToTrace(blocks: AssistantBlock[], ids: string[]): void {
  if (ids.length === 0) return;
  const start = openSegmentStart(blocks);
  const existing = blocks.slice(start).find(b => b.kind === 'trace');
  if (existing?.kind === 'trace') existing.toolCallIds.push(...ids);
  else blocks.splice(start, 0, { kind: 'trace', toolCallIds: [...ids] });
}

const NARRATION_MAX_CHARS = 120;
const MARKDOWN_START = /^(?:[#>|\-*]|\d+\.)/;

/**
 * Is this fragment's text a "let me load X, then do Y" process aside?
 *
 * Such sentences are the model talking between two tool calls; they are not part
 * of the answer, so the merge folds them into the turn's collapsed trace instead
 * of the answer bubble. `toolCalls` are the calls that FOLLOW the text, which
 * need not be on the same message — see `followingToolCalls`. Deliberately
 * conservative: it must be followed by ordinary tool calls (text before a gate
 * card stays in the prose), be one short line, and
 * carry no markdown structure, so plans, tables and bolded summaries never fold.
 * A false positive only moves a sentence into the (expandable) trace.
 */
export function isProcessNarration(text: string, toolCalls: TranscriptMessage['toolCalls']): boolean {
  if (!toolCalls?.length) return false;
  if (toolCalls.some(tc => tc.function?.name && INLINE_TOOL_NAMES.has(tc.function.name))) return false;
  const t = text.trim();
  if (!t || t.length > NARRATION_MAX_CHARS || /[\r\n]/.test(t)) return false;
  if (MARKDOWN_START.test(t)) return false;
  if (t.includes('```') || t.includes('|') || t.includes('**')) return false;
  return true;
}

/**
 * Fold one fragment into a run's block sequence.
 *
 * Text lands in the open text block when there is one — so consecutive
 * text-only fragments still join with the same blank line, and a turn without
 * gates still produces a single bubble. A gate closes that block and the segment,
 * which is the whole point: text and trace calls after it land BELOW the card.
 * A fragment's trace calls join the segment before its gates — they were
 * declared alongside the card, not in answer to it.
 */
function appendBlocks(
  blocks: AssistantBlock[],
  text: string,
  gateIds: string[],
  traceIds: string[],
): void {
  if (text) {
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'text') last.text = `${last.text}\n\n${text}`;
    else blocks.push({ kind: 'text', text });
  }
  addToTrace(blocks, traceIds);
  if (gateIds.length > 0) blocks.push({ kind: 'gates', toolCallIds: gateIds });
}

/**
 * The tool calls that follow this fragment's text — what `isProcessNarration`
 * must judge it against.
 *
 * Usually the fragment's own `toolCalls`. But whether the text and the calls it
 * precedes share ONE message depends on how the messages reached us, not on what
 * the model did:
 *
 * - live, delta payload: one message — `ag_ui_strands` sets the call's
 *   `parentMessageId` to the text's id, so CopilotKit attaches it there;
 * - live, snapshot payload (the first turn of a new thread, when the Runtime has
 *   no session history yet): two — the wrapper closes the text message before the
 *   call and mints a new id for it (`agent.py`, `message_id = str(uuid.uuid4())`
 *   before `ToolCallStartEvent`), so the text arrives with no calls of its own;
 * - restored history: two — `buildReplayMessages` emits the prose row and the
 *   `tool_use` row as separate messages.
 *
 * So a text-only fragment borrows the calls of the next assistant fragment in the
 * run, provided that one has no text of its own (then it is the same model step,
 * split in transit). `role:'tool'` rows are skipped: they trail calls, never
 * precede them. Without this the second and third shapes left every aside in the
 * answer bubble.
 */
function followingToolCalls(
  messages: TranscriptMessage[],
  i: number,
  text: string,
): TranscriptMessage['toolCalls'] {
  const own = messages[i].toolCalls;
  if (own?.length || !text) return own;
  for (let j = i + 1; j < messages.length; j++) {
    const next = messages[j];
    if (next.role === 'tool') continue;
    if (next.role !== 'assistant' || messageText(next.content)) return undefined;
    return next.toolCalls;
  }
  return undefined;
}

/** Route a fragment's text to `narrations` or `blocks`, and its gates to `blocks`. */
function foldFragment(
  head: TranscriptMessage,
  m: TranscriptMessage,
  text: string,
  following: TranscriptMessage['toolCalls'],
): void {
  if (isProcessNarration(text, following)) {
    head.narrations!.push({ beforeToolCallId: following![0].id, text: text.trim() });
    appendBlocks(head.blocks!, '', gateIdsOf(m), traceIdsOf(m));
  } else {
    appendBlocks(head.blocks!, text, gateIdsOf(m), traceIdsOf(m));
  }
}

/**
 * Give a finished turn its narrations back as the answer when nothing else is.
 *
 * `isProcessNarration` can only judge one fragment at a time: "a short line
 * next to a tool call" is usually an aside, because the real answer follows in
 * a later fragment. When the turn ends right after a tool call (stop, error,
 * max-tokens, or a final reply sent alongside one), no later fragment comes,
 * and folding would leave the answer empty with its only text hidden in the
 * collapsed trace. Applied by the view once the turn stops streaming — not in
 * the merge, which cannot tell "ended here" from "more is coming", so a live
 * turn's asides do not flash into the bubble and then jump out again.
 */
export function unfoldNarrationsIfNoAnswer(
  blocks: AssistantBlock[],
  narrations: Narration[],
): { blocks: AssistantBlock[]; narrations: Narration[] } {
  if (narrations.length === 0 || blocks.some(b => b.kind === 'text')) return { blocks, narrations };
  // After the leading trace, if any: the trace heads its segment.
  const at = blocks[0]?.kind === 'trace' ? 1 : 0;
  return {
    blocks: [
      ...blocks.slice(0, at),
      { kind: 'text', text: narrations.map(n => n.text).join('\n\n') },
      ...blocks.slice(at),
    ],
    narrations: [],
  };
}

/**
 * Move a run's `suggest_replies` cards to the end of its block sequence.
 *
 * The options answer the question the turn ends on, so they belong under all of
 * its text. Live, that is where the model's call order already puts them. Restored
 * history does not: `buildReplayMessages` emits each `tool_use` row before the
 * turn's `agent_final_response` row, which would draw the buttons ABOVE the
 * question they answer. Pinning them last makes both paths agree. A text block
 * left adjacent to another by the move is joined to it, as consecutive text
 * fragments are, and two segments the move fuses share one trace.
 */
function trailSuggestions(head: TranscriptMessage): void {
  const ids = new Set(
    (head.toolCalls ?? []).filter(tc => tc.function?.name === SUGGEST_REPLIES_TOOL).map(tc => tc.id),
  );
  if (ids.size === 0 || !head.blocks) return;
  const moved: string[] = [];
  const blocks: AssistantBlock[] = [];
  for (const b of head.blocks) {
    if (b.kind === 'gates') {
      const keep = b.toolCallIds.filter(id => !ids.has(id));
      moved.push(...b.toolCallIds.filter(id => ids.has(id)));
      if (keep.length > 0) blocks.push({ kind: 'gates', toolCallIds: keep });
      continue;
    }
    if (b.kind === 'trace') {
      addToTrace(blocks, b.toolCallIds);
      continue;
    }
    const last = blocks[blocks.length - 1];
    if (last?.kind === 'text') last.text = `${last.text}\n\n${b.text}`;
    else blocks.push({ ...b });
  }
  if (moved.length > 0) blocks.push({ kind: 'gates', toolCallIds: moved });
  head.blocks = blocks;
}

/**
 * Merge each run of consecutive assistant messages into one.
 *
 * `role:'tool'` messages do not break a run. They sit between an assistant's
 * tool call and the assistant's follow-up, and the view renders nothing for them
 * (`renderMessageBlock` handles assistant / user / activity / reasoning only), so
 * treating them as a separator would split one answer in two for exactly the
 * turns that used a tool. They are kept in the output because
 * `CopilotChatToolCallsView` resolves a tool call's status by searching the same
 * list for the matching `role:'tool'` message.
 *
 * The merged message keeps the FIRST fragment's id, so that ids stay stable as
 * more fragments arrive during streaming — keying on the last one would change
 * the React key mid-turn and remount the bubble. `mergedIds` carries the rest.
 *
 * `blocks` records the fragment boundaries that matter to the layout; `content`
 * stays the whole run's text, because everything else that reads a message reads
 * that (the change-detection signal, CopilotKit's own memo comparator).
 */
export function coalesceAssistantRuns(messages: TranscriptMessage[]): CoalescedTranscript {
  const out: TranscriptMessage[] = [];
  const mergedIds = new Map<string, string[]>();
  let head: TranscriptMessage | null = null;

  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role === 'assistant') {
      const text = messageText(m.content);
      const following = followingToolCalls(messages, i, text);
      if (head) {
        const headText = messageText(head.content);
        // Blank line between fragments, not bare concatenation. Each fragment is a
        // separate TEXT_MESSAGE block from the runtime — streaming deltas append
        // inside one message, so two fragments mean the model stopped and resumed,
        // typically around a tool call ("让我查一下" / tool / "查到了…"). Glued, markdown
        // renders them as one run-on paragraph: `让我查一下查到了…`.
        head.content = headText ? (text ? `${headText}\n\n${text}` : headText) : text;
        if (m.toolCalls?.length) {
          head.toolCalls = [...(head.toolCalls ?? []), ...m.toolCalls];
        }
        foldFragment(head, m, text, following);
        mergedIds.get(head.id)!.push(m.id);
        continue;
      }
      head = {
        ...m,
        content: text,
        ...(m.toolCalls ? { toolCalls: [...m.toolCalls] } : {}),
        blocks: [],
        narrations: [],
      };
      foldFragment(head, m, text, following);
      mergedIds.set(head.id, [head.id]);
      out.push(head);
      continue;
    }
    // A tool result belongs to the run it resolves — pass it through without
    // closing the run.
    if (m.role === 'tool') {
      out.push(m);
      continue;
    }
    head = null;
    out.push(m);
  }

  for (const m of out) if (m.blocks) trailSuggestions(m);
  return { messages: out, mergedIds };
}

/**
 * Re-key a side table from source-row ids onto the merged message ids the view
 * actually renders.
 *
 * Later entries win on conflict, which is the useful direction: within one turn
 * the final response is the last fragment and carries the turn-level facts
 * (⏱ elapsed, file outputs), while an earlier tool-call fragment has none.
 */
export function resolveExtrasByRun<T extends object>(
  extras: Map<string, T>,
  mergedIds: Map<string, string[]>,
): Map<string, T> {
  if (extras.size === 0) return extras;
  const resolved = new Map<string, T>();
  const consumed = new Set<string>();
  for (const [headId, ids] of mergedIds) {
    let merged: T | undefined;
    for (const id of ids) {
      consumed.add(id);
      const found = extras.get(id);
      if (found) merged = merged ? { ...merged, ...found } : found;
    }
    if (merged) resolved.set(headId, merged);
  }
  // Non-assistant rows (user messages carry the KB labels) never appear in
  // `mergedIds`; pass their entries through untouched. Ids that WERE consumed by
  // a run are dropped rather than passed through — re-adding a swallowed
  // fragment's id would leave a second entry claiming the same turn's facts.
  for (const [id, value] of extras) {
    if (!consumed.has(id)) resolved.set(id, value);
  }
  return resolved;
}

/**
 * Has the turn in progress produced an assistant message yet?
 *
 * Decides whether the panel shows its standalone "Thinking..." affordance: once
 * the answer's own bubble exists, that bubble carries the open trace and the
 * spinning avatar, and a second indicator would be a duplicate.
 *
 * Scans back to the last `user` message rather than checking the tail, because
 * `role:'tool'` rows trail the assistant that called them — "the last message is
 * not an assistant" is true for every turn that ran a backend tool, which is
 * exactly when the duplicate would appear.
 */
export function turnHasAssistantReply(messages: TranscriptMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const role = messages[i].role;
    if (role === 'user') return false;
    if (role === 'assistant') return true;
  }
  return false;
}

// ── Attachment metadata parser (strips Gateway storage format) ──────────────
//
// A user message's stored content can carry its attachments two ways: the
// `[Attachments]` block the Gateway appends when persisting, and the "📎 a, b"
// line the panel appends locally on send. Both are display metadata rather than
// part of what the user typed, so both are lifted out here and rendered as chips.

const ATTACHMENTS_BLOCK_REGEX = /\s*\[Attachments\]\s*([\s\S]*)$/;
const ATTACHMENT_ITEM_REGEX = /-\s*\[([^\]]+)\]/g;
const PAPERCLIP_REGEX = /\n*📎\s*(.+)$/;

export function parseUserContent(content: string): { text: string; attachedFiles: string[] } {
  const attachedFiles: string[] = [];

  let text = content;
  const blockMatch = text.match(ATTACHMENTS_BLOCK_REGEX);
  if (blockMatch) {
    const block = blockMatch[1];
    let itemMatch;
    while ((itemMatch = ATTACHMENT_ITEM_REGEX.exec(block)) !== null) {
      attachedFiles.push(itemMatch[1]);
    }
    ATTACHMENT_ITEM_REGEX.lastIndex = 0;
    text = text.replace(ATTACHMENTS_BLOCK_REGEX, '').trim();
  }

  const clipMatch = text.match(PAPERCLIP_REGEX);
  if (clipMatch) {
    clipMatch[1].split(',').forEach(f => attachedFiles.push(f.trim()));
    text = text.replace(PAPERCLIP_REGEX, '').trim();
  }

  return { text, attachedFiles };
}
